import { useEffect, useRef } from 'react';
import { useDebouncedCallback } from '@react-hookz/web';
import { atom, useAtom, useAtomValue } from 'jotai';

const activeAnchorsAtom = atom<string[]>([]);
/**
 * Find the active heading of page
 *
 * It selects the top heading by default, and the last item when reached the bottom of page.
 *
 * @param watch - An array of element ids to watch
 */
export function useAnchorObserver(watch: string[]) {
  const [activeAnchors, setActiveAnchor] = useAtom(activeAnchorsAtom);
  const activeAnchorsRef = useRef(activeAnchors);
  activeAnchorsRef.current = activeAnchors;
  const onScroll = useDebouncedCallback(
    (): void => {
      const element = document.scrollingElement;
      if (!element) {
        return;
      }
      if (
        element.scrollTop + element.clientHeight >=
        element.scrollHeight - 6
      ) {
        const newAnchors =
          activeAnchorsRef.current.length > 0
            ? watch.slice(watch.indexOf(activeAnchorsRef.current[0]!))
            : watch.slice(-1);

        // Skip update if arrays are equal
        const areArraysEqual =
          newAnchors.length === activeAnchorsRef.current.length &&
          newAnchors.every(
            (id, index) => id === activeAnchorsRef.current[index],
          );

        if (!areArraysEqual) {
          setActiveAnchor(newAnchors);
        }
      }
    },
    [],
    500,
  );
  useEffect(() => {
    let visible: string[] = [];
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !visible.includes(entry.target.id)) {
            visible = [...visible, entry.target.id];
          } else if (
            !entry.isIntersecting &&
            visible.includes(entry.target.id)
          ) {
            visible = visible.filter((v) => v !== entry.target.id);
          }
        }

        if (visible.length > 0) {
          const currentAnchors = activeAnchorsRef.current;
          const areArraysEqual =
            visible.length === currentAnchors.length &&
            visible.every((id, index) => id === currentAnchors[index]);

          if (!areArraysEqual) {
            setActiveAnchor(visible);
          }
        }
      },
      {
        rootMargin: `-20px 0% -40% 0%`,
        threshold: 1,
      },
    );

    for (const heading of watch) {
      const element = document.getElementById(heading);

      if (element) {
        observer.observe(element);
      }
    }

    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [watch, onScroll]);
}

export function useActiveAnchors() {
  return useAtomValue(activeAnchorsAtom);
}
